import { useState, useRef, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const COLORS = [
  "hsl(var(--chart-1))",
  "hsl(var(--chart-2))",
  "hsl(var(--chart-3))",
  "hsl(var(--chart-4))",
  "hsl(var(--chart-5))",
];

export function PizzaWheel() {
  const [rawText, setRawText] = useState("");
  const [isSpinning, setIsSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [winnerIndex, setWinnerIndex] = useState<number | null>(null);
  const animationRef = useRef<number | null>(null);

  // Parse entries from textarea (one per line, dedupe, filter empty)
  const entries = useMemo(() => {
    const lines = rawText
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
    return Array.from(new Set(lines));
  }, [rawText]);

  const canSpin = entries.length >= 2 && !isSpinning;

  // Calculate slice angle for SVG paths
  const sliceAngle = entries.length > 0 ? 360 / entries.length : 0;

  // Generate SVG path for a pie slice
  const createSlicePath = (index: number): string => {
    const startAngle = (index * sliceAngle - 90) * (Math.PI / 180);
    const endAngle = ((index + 1) * sliceAngle - 90) * (Math.PI / 180);
    const radius = 180;

    const x1 = 200 + radius * Math.cos(startAngle);
    const y1 = 200 + radius * Math.sin(startAngle);
    const x2 = 200 + radius * Math.cos(endAngle);
    const y2 = 200 + radius * Math.sin(endAngle);

    const largeArc = sliceAngle > 180 ? 1 : 0;

    return `M 200 200 L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;
  };

  // Calculate text position for each slice
  const getTextPosition = (index: number) => {
    const angle = (index * sliceAngle + sliceAngle / 2 - 90) * (Math.PI / 180);
    const radius = 120;
    return {
      x: 200 + radius * Math.cos(angle),
      y: 200 + radius * Math.sin(angle),
      rotation: index * sliceAngle + sliceAngle / 2,
    };
  };

  // Easing function (ease-out cubic)
  const easeOutCubic = (t: number): number => {
    return 1 - Math.pow(1 - t, 3);
  };

  const spinWheel = () => {
    if (!canSpin) return;

    setIsSpinning(true);
    setWinnerIndex(null);

    // Random target: 3-5 full spins + random offset to land on a slice
    const baseTurns = 3 + Math.random() * 2;
    const randomSliceIndex = Math.floor(Math.random() * entries.length);
    const sliceCenter = randomSliceIndex * sliceAngle + sliceAngle / 2;
    const targetRotation = rotation + baseTurns * 360 + (360 - sliceCenter);

    const startRotation = rotation;
    const startTime = performance.now();
    const duration = 4000; // 4 seconds

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeOutCubic(progress);

      const currentRotation = startRotation + (targetRotation - startRotation) * easedProgress;
      setRotation(currentRotation);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        // Animation complete - determine winner
        const normalizedRotation = currentRotation % 360;
        const pointerAngle = (360 - normalizedRotation) % 360;
        const winner = Math.floor(pointerAngle / sliceAngle) % entries.length;
        
        setWinnerIndex(winner);
        setIsSpinning(false);
        animationRef.current = null;
      }
    };

    animationRef.current = requestAnimationFrame(animate);
  };

  return (
    <div className="container mx-auto p-8">
      <Card className="max-w-6xl mx-auto">
        <CardHeader className="text-center">
          <CardTitle className="text-4xl font-bold">🍕 Pizza Wheel of Fortune</CardTitle>
          <CardDescription>
            Enter your favorite pizza places and let fate decide!
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-8">
            {/* Left column - Controls */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="pizza-input">Pizza Places (one per line)</Label>
                <Textarea
                  id="pizza-input"
                  value={rawText}
                  onChange={(e) => setRawText(e.target.value)}
                  disabled={isSpinning}
                  className="min-h-[200px] font-mono"
                  placeholder="Papa John's&#10;Domino's&#10;Pizza Hut"
                />
              </div>

              <div className="text-sm text-muted-foreground">
                {entries.length === 0 && "Add at least 2 pizza places to spin"}
                {entries.length === 1 && "Add at least one more pizza place"}
                {entries.length >= 2 && `${entries.length} pizza places ready`}
              </div>

              <Button
                onClick={spinWheel}
                disabled={!canSpin}
                size="lg"
                className="w-full"
              >
                {isSpinning ? "Spinning..." : "Spin the Wheel! 🍕"}
              </Button>

              {winnerIndex !== null && (
                <div className="p-4 bg-primary/10 border border-primary rounded-lg text-center">
                  <div className="text-sm font-medium text-muted-foreground mb-1">
                    Winner:
                  </div>
                  <div className="text-2xl font-bold text-primary">
                    🎉 {entries[winnerIndex]} 🎉
                  </div>
                </div>
              )}
            </div>

            {/* Right column - Wheel */}
            <div className="flex items-center justify-center">
              <div className="relative">
                {/* Pointer at top */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-2 z-10">
                  <div className="w-0 h-0 border-l-[20px] border-l-transparent border-r-[20px] border-r-transparent border-t-[30px] border-t-primary drop-shadow-lg" />
                </div>

                {/* The wheel */}
                <svg
                  width="400"
                  height="400"
                  viewBox="0 0 400 400"
                  className="drop-shadow-xl"
                >
                  <g
                    style={{
                      transform: `rotate(${rotation}deg)`,
                      transformOrigin: "200px 200px",
                      transition: isSpinning ? "none" : "transform 0.3s ease-out",
                    }}
                  >
                    {entries.length > 0 ? (
                      entries.map((entry, index) => {
                        const textPos = getTextPosition(index);
                        return (
                          <g key={index}>
                            <path
                              d={createSlicePath(index)}
                              fill={COLORS[index % COLORS.length]}
                              stroke="white"
                              strokeWidth="2"
                            />
                            <text
                              x={textPos.x}
                              y={textPos.y}
                              fill="white"
                              fontSize="14"
                              fontWeight="bold"
                              textAnchor="middle"
                              dominantBaseline="middle"
                              style={{
                                transform: `rotate(${textPos.rotation}deg)`,
                                transformOrigin: `${textPos.x}px ${textPos.y}px`,
                              }}
                            >
                              {entry.length > 15 ? entry.substring(0, 12) + "..." : entry}
                            </text>
                          </g>
                        );
                      })
                    ) : (
                      <circle cx="200" cy="200" r="180" fill="hsl(var(--muted))" />
                    )}
                    {/* Center circle */}
                    <circle cx="200" cy="200" r="30" fill="white" stroke="hsl(var(--border))" strokeWidth="2" />
                  </g>
                </svg>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
